import { GallerySideImage, GallerySlideContent } from './gallery-data';

// Contratto dell'API getHomeSlides/{lang}/{domain} (testi già tradotti nella lingua richiesta)
export type HomeSlideLayerType = 'titolo' | 'testo' | 'marca' | 'bottone' | 'immagine';
export type HomeSlideVertical = 'alto' | 'centro' | 'basso';
export type HomeSlideAlign = 'sinistra' | 'centro' | 'destra';
// classi Bootstrap di dimensione del testo (non tag: niente intestazioni nella gerarchia della pagina)
export type HomeSlideSize = 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

export interface HomeSlideLayerApi {
    tipo: HomeSlideLayerType;
    // posizione dell'elemento sopra l'immagine (assenti nelle risposte in cache precedenti al deploy)
    posizione_verticale?: HomeSlideVertical;
    allineamento?: HomeSlideAlign;
    // null = dimensione standard del tipo di layer
    dimensione?: HomeSlideSize | null;
    testo: string | null;
    url: string | null;
    immagine: string | null;
    alt: string | null;
    colore: string | null;
    animazione: string | null;
    ritardo: number | null;
    stile: string | null;
}

export interface HomeSlideApi {
    idslide: number;
    immagine: string;
    width: number;
    height: number;
    durata_factor: number;
    // banner della colonna di destra: null = banner standard
    img_2?: string | null;
    img_2_link?: string | null;
    img_3?: string | null;
    img_3_link?: string | null;
    layers: HomeSlideLayerApi[];
}

// Effetti animate.css ammessi (i dati arrivano da DB: niente classi arbitrarie)
const ANIMATIONS = new Set<string>([
    'backInDown', 'backInLeft', 'backInRight', 'backInUp',
    'bounceIn', 'bounceInDown', 'bounceInLeft', 'bounceInRight', 'bounceInUp',
    'fadeIn', 'fadeInDown', 'fadeInDownBig', 'fadeInLeft', 'fadeInLeftBig',
    'fadeInRight', 'fadeInRightBig', 'fadeInUp', 'fadeInUpBig',
    'flipInX', 'flipInY', 'jackInTheBox', 'lightSpeedInLeft', 'lightSpeedInRight',
    'rollIn', 'rotateIn', 'slideInDown', 'slideInLeft', 'slideInRight', 'slideInUp',
    'zoomIn', 'zoomInDown', 'zoomInLeft', 'zoomInRight', 'zoomInUp'
]);

// Il bottone sta sopra l'area di drag della gallery: non deve avviare lo swipe
const CTA_STOP_DRAG = 'onmousedown="event.stopPropagation()" onmousemove="event.stopPropagation()" onmouseup="event.stopPropagation()"';
const CTA_STYLE = 'background-color: #f8f00c; color: #000; border-radius: 16px; padding: 10px;';

function esc(value: string): string {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function safeColor(color: string | null, fallback: string): string {
    return color && /^(#[0-9a-f]{3,8}|[a-z]+)$/i.test(color) ? color : fallback;
}

// CSS inline: solo dichiarazioni, niente caratteri che chiudono l'attributo o aprono tag/url
function safeStyle(style: string | null): string {
    return style && !/["<>\\]|url\s*\(|expression/i.test(style) ? style.trim() : '';
}

function safeUrl(url: string | null): string {
    return url && /^(\/(?!\/)|https?:\/\/)/i.test(url) ? esc(url) : '#';
}

// Path relativo al sito (es. images/home_gallery/...) oppure URL assoluto http(s)
function safeSrc(src: string): string {
    return /^([\w\-./]+|https?:\/\/\S+)$/i.test(src) && !src.startsWith('//') ? esc(src) : '';
}

function animClasses(layer: HomeSlideLayerApi): string {
    if (!layer.animazione || !ANIMATIONS.has(layer.animazione)) {
        return '';
    }
    let classes = ` animate__animated animate__${layer.animazione}`;
    // 0.5s → animate__delay-05s (convenzione delle slide storiche), interi 1-5 → classi animate.css
    if (layer.ritardo === 0.5) {
        classes += ' animate__delay-05s';
    } else if (layer.ritardo && Number.isInteger(layer.ritardo) && layer.ritardo >= 1 && layer.ritardo <= 5) {
        classes += ` animate__delay-${layer.ritardo}s`;
    }
    return classes;
}

// Colore leggibile (nero/bianco) sopra uno sfondo esadecimale, per il testo dei bottoni (WCAG)
function contrastText(background: string): string {
    const hex = background.replace('#', '');
    const full = hex.length === 3 || hex.length === 4 ? hex.slice(0, 3).split('').map((c) => c + c).join('') : hex.slice(0, 6);
    if (!/^[0-9a-f]{6}$/i.test(full)) return '#000';
    const [r, g, b] = [0, 2, 4].map((i) => {
        const c = parseInt(full.slice(i, i + 2), 16) / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return luminance > 0.179 ? '#000' : '#fff';
}

const TEXT_ALIGN: Record<HomeSlideAlign, string> = { sinistra: 'left', centro: 'center', destra: 'right' };
// margini del box del bottone (largo 200px) per allinearlo
const BUTTON_MARGIN: Record<HomeSlideAlign, string> = { sinistra: '0 auto 0 0', centro: '0 auto', destra: '0 0 0 auto' };

function styleAttr(...declarations: string[]): string {
    const style = declarations.filter(Boolean).join(' ');
    return style ? ` style="${style}"` : '';
}

function layerSize(layer: HomeSlideLayerApi): HomeSlideSize | null {
    return layer.dimensione && /^h[2-6]$/.test(layer.dimensione) ? layer.dimensione : null;
}

/**
 * Un solo stile per tutte le slide (quello della slide FAAC): senza dimensione titolo e marca
 * mantengono le classi storiche slide-ricambi-originali / slide-marca (dimensioni responsive in scss).
 */
function buildLayer(layer: HomeSlideLayerApi, align: HomeSlideAlign, imageIndex: number): string {
    const anim = animClasses(layer);
    const stile = safeStyle(layer.stile);
    const size = layerSize(layer);
    // con centro (default) non si aggiunge nulla: markup identico alle slide storiche
    const textAlign = align !== 'centro' ? `text-align: ${TEXT_ALIGN[align]};` : '';
    const color = layer.colore ? `color: ${safeColor(layer.colore, '#fff')};` : '';

    if (layer.tipo === 'immagine') {
        const src = layer.immagine ? safeSrc(layer.immagine) : '';
        if (!src) return '';
        const n = String(imageIndex).padStart(3, '0');
        return `<img class="img-fluid slide-img-${n}${anim}" src="${src}" alt="${esc(layer.alt ?? '')}"${styleAttr(stile)} />`;
    }

    if (!layer.testo) return '';
    const testo = esc(layer.testo);

    switch (layer.tipo) {
        case 'titolo':
            return `<p class="${size ?? 'h2'} text-transform-none${size ? '' : ' slide-ricambi-originali'}${anim}"${styleAttr(color, textAlign, stile)}>${testo}</p>`;
        case 'marca':
            return size
                ? `<p class="${size} text-transform-none${anim}"${styleAttr(color, textAlign, stile)}>${testo}</p>`
                : `<h3 class="slide-marca${anim}"${styleAttr(color, textAlign, stile)}>${testo}</h3>`;
        case 'testo':
            return `<div class="flex text-transform-none${size ? ' ' + size : ''}${anim}"${styleAttr(color, textAlign, stile)}>${testo}</div>`;
        case 'bottone': {
            const inner = `width: 200px; margin: ${BUTTON_MARGIN[align]}; padding-top: 10px; text-align: center;`;
            // colore = sfondo del bottone, testo nero o bianco in base al contrasto
            const background = layer.colore ? safeColor(layer.colore, '') : '';
            const ctaStyle = background
                ? `background-color: ${background}; color: ${contrastText(background)}; border-radius: 16px; padding: 10px;`
                : CTA_STYLE;
            return `<div class="flex text-transform-none${anim}"><div${styleAttr(inner, stile)}>`
                + `<a ${CTA_STOP_DRAG} style="${ctaStyle}" href="${safeUrl(layer.url)}" class="hov_white${size ? ' ' + size : ''}">${testo}</a></div></div>`;
        }
        default:
            return '';
    }
}

const VERTICALS: HomeSlideVertical[] = ['alto', 'centro', 'basso'];

function layerVertical(layer: HomeSlideLayerApi): HomeSlideVertical {
    return layer.posizione_verticale && VERTICALS.includes(layer.posizione_verticale) ? layer.posizione_verticale : 'centro';
}

function layerAlign(layer: HomeSlideLayerApi): HomeSlideAlign {
    return layer.allineamento && layer.allineamento in TEXT_ALIGN ? layer.allineamento : 'centro';
}

/**
 * HTML dell'overlay di una slide a partire dai layer.
 * Ogni layer ha posizione verticale e allineamento propri: i layer sono raggruppati in tre zone
 * (alto / centro / basso) sovrapposte alla slide. Se sono tutti al centro il contenuto resta
 * centrato dall'overlay, senza zone.
 */
export function buildSlideHtml(slide: HomeSlideApi): string {
    let imageIndex = 0;
    const rendered = (slide.layers ?? []).map((layer) => {
        const align = layerAlign(layer);
        return {
            vertical: layerVertical(layer),
            html: buildLayer(layer, align, layer.tipo === 'immagine' ? ++imageIndex : 0)
        };
    });
    const wrap = (layers: typeof rendered) =>
        `<div class="banner-layer d-flex flex-column">${layers.map((r) => r.html).join('')}</div>`;

    if (rendered.every((r) => r.vertical === 'centro')) {
        return wrap(rendered);
    }

    // zone a tutta slide (stili .slide-pos in slide.component.scss)
    return VERTICALS
        .map((vertical) => ({ vertical, layers: rendered.filter((r) => r.vertical === vertical) }))
        .filter((zone) => zone.layers.length > 0)
        .map((zone) => `<div class="slide-pos slide-pos-${zone.vertical}">${wrap(zone.layers)}</div>`)
        .join('');
}

// Valori usati in binding Angular (non in HTML generato): solo validazione, niente escape
function sideImage(src: string | null | undefined, link: string | null | undefined): GallerySideImage | null {
    if (!src || !/^([\w\-./]+|https:\/\/\S+)$/i.test(src) || src.startsWith('//')) {
        return null;
    }
    return { src, link: link && /^(\/(?!\/)|https:\/\/)/i.test(link) ? link : null };
}

export function toGallerySlide(slide: HomeSlideApi): GallerySlideContent {
    return {
        imageUrl: slide.immagine,
        width: slide.width,
        height: slide.height,
        html_content: buildSlideHtml(slide),
        trad_keys: [],
        durationFactor: slide.durata_factor || 1,
        sideImages: [sideImage(slide.img_2, slide.img_2_link), sideImage(slide.img_3, slide.img_3_link)]
    };
}
